/**
 * Client des membres et des invitations.
 *
 * Une invitation n'est pas envoyée par courriel : Capalys n'a pas encore de transport de
 * messages, et en simuler un ferait croire à un administrateur qu'il a invité quelqu'un
 * qui ne recevra rien. Le lien est donc rendu à l'écran, une seule fois, à charge de le
 * transmettre par le canal habituel de l'usine — qui est de toute façon Teams ou le
 * couloir.
 */

import { apiFetch, detailOf } from "./http";

export const ROLE_LABELS: Record<string, string> = {
  admin: "Administrateur",
  engineer: "Ingénieur",
  technician: "Technicien",
};

/** Ce que chaque rôle peut faire, dit en une ligne — la question qu'on se pose en
 *  invitant quelqu'un est « et il pourra faire quoi ? », pas « quel est son grade ». */
export const ROLE_HINTS: Record<string, string> = {
  admin: "Configure, invite, retire. Tout ce qu'un ingénieur peut faire.",
  engineer: "Dépose des fichiers, saisit les tolérances, ajuste les limites.",
  technician: "Consulte et exporte. Ne modifie rien.",
};

export const ROLES = ["admin", "engineer", "technician"] as const;

export interface Member {
  user_id: string;
  email: string;
  full_name: string | null;
  role: string;
  joined_at: string;
  /** `null` pour quelqu'un qui n'est jamais venu : une invitation acceptée mais jamais
   *  suivie d'un usage se lit ici, et nulle part ailleurs. */
  last_login_at: string | null;
}

export interface Invitation {
  id: string;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  pending: boolean;
}

/** Une invitation qui vient d'être émise. `token` n'apparaît que là, et jamais plus. */
export interface IssuedInvitation extends Invitation {
  token: string;
}

/** Ce qu'un porteur de lien apprend avant de s'engager. */
export interface InvitationTarget {
  org_name: string;
  email: string;
  role: string;
  expires_at: string;
}

export async function listMembers(): Promise<Member[]> {
  const response = await apiFetch("/v1/members");
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as Member[];
}

export async function setRole(userId: string, role: string): Promise<void> {
  const response = await apiFetch(`/v1/members/${userId}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!response.ok) throw new Error(await detailOf(response));
}

export async function removeMember(userId: string): Promise<void> {
  const response = await apiFetch(`/v1/members/${userId}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error(await detailOf(response));
}

export async function listInvitations(): Promise<Invitation[]> {
  const response = await apiFetch("/v1/invitations");
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as Invitation[];
}

export async function createInvitation(
  email: string,
  role: string,
): Promise<IssuedInvitation> {
  const response = await apiFetch("/v1/invitations", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, role }),
  });
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as IssuedInvitation;
}

export async function revokeInvitation(id: string): Promise<void> {
  const response = await apiFetch(`/v1/invitations/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error(await detailOf(response));
}

export async function describeInvitation(
  token: string,
): Promise<InvitationTarget> {
  const response = await apiFetch(
    `/v1/invitations/token/${encodeURIComponent(token)}`,
  );
  if (!response.ok) throw new Error(await detailOf(response));
  return (await response.json()) as InvitationTarget;
}

export async function acceptInvitation(
  token: string,
  password: string,
  fullName: string | null,
): Promise<void> {
  const response = await apiFetch(
    `/v1/invitations/token/${encodeURIComponent(token)}/accept`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password, full_name: fullName }),
    },
  );
  if (!response.ok) throw new Error(await detailOf(response));
}

/**
 * Lien d'invitation, construit sur l'origine courante.
 *
 * Le jeton voyage en paramètre de requête et non dans le chemin : les fichiers statiques
 * sont servis par le Worker, et une URL profonde comme `/invitation/xyz` demanderait un
 * repli SPA côté serveur. Un paramètre sur la racine fonctionne partout, tout de suite, y
 * compris en développement.
 */
export function invitationLink(token: string): string {
  return `${window.location.origin}/?invitation=${encodeURIComponent(token)}`;
}

/** Jeton présent dans l'URL, s'il y en a un. */
export function invitationFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get("invitation");
}

/** Format court et non ambigu, le même que dans l'écran Projets. */
export function frenchDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
